import type { AuthSessionChange } from "@buildpy/core";
import { isRejectedAuthError } from "@buildpy/core/lib/auth-unavailable";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import { publicEnv } from "@/lib/public-env";

let client: SupabaseClient | null = null;

// 첫 주소 — 클라이언트가 만들어지며 #access_token 을 지우기 전에 잡아 둔다(재설정 메일 착지 판정용).
export const BOOT_HREF = typeof window === "undefined" ? "" : window.location.href;

export type AuthCallback = { kind: "recovery" } | { kind: "signup" } | { kind: "error"; code: string } | { kind: "none" };

// 가입 확인 메일 링크가 붙이는 type — generateLink(signup) 는 signup, 메일 변경 판은 email, 확인 전 계정 다시 받기는 magiclink.
const SIGNUP_LINK_TYPES = new Set(["signup", "email", "magiclink"]);

// 메일 링크가 붙여 온 값(implicit) — 해시 우선, 오류는 쿼리로 올 때도 있다 · 토큰 값은 돌려주지 않는다.
export const authCallbackOf = (href: string): AuthCallback => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { kind: "none" };
  }
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
  const error = hash.get("error_code") ?? hash.get("error") ?? url.searchParams.get("error_code") ?? url.searchParams.get("error");
  if (error) return { kind: "error", code: error };
  if (hash.get("access_token") && hash.get("type") === "recovery") return { kind: "recovery" };
  if (hash.get("access_token") && SIGNUP_LINK_TYPES.has(hash.get("type") ?? "")) return { kind: "signup" };
  return { kind: "none" };
};

// 브라우저 클라이언트는 세션 보관·갱신만 한다 — 데이터 읽기·쓰기는 전부 /api 로 간다.
export const getSupabase = (): SupabaseClient => {
  if (!client) {
    const env = publicEnv();
    client = createClient(env.supabaseUrl, env.supabaseKey);
  }
  return client;
};

export const readAccessToken = async (): Promise<string | null> => {
  const { data } = await getSupabase().auth.getSession();
  return data.session?.access_token ?? null;
};

// 거절 판정은 서버와 한 벌(core auth-unavailable) — 테스트가 여기서 가져다 쓰므로 다시 내보낸다.
export { isRejectedAuthError };

// 거절 판정 대기 한도 — 인증 서버가 답이 없으면 거절 아님으로 보고 호출자(로그인 이동)를 잇는다(401 뒤 로그인 화면이 늦지 않게 짧게).
export const SESSION_CHECK_TIMEOUT_MS = 1500;

const checkSession = async (accessToken: string): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<false>((resolve) => {
    timer = setTimeout(() => {
      console.error("[session]", `getUser ${SESSION_CHECK_TIMEOUT_MS}ms 초과 — 거절 아님으로 본다`);
      resolve(false);
    }, SESSION_CHECK_TIMEOUT_MS);
  });
  // 판정 대상 토큰을 넘긴다 — 인자 없는 getUser() 는 그 사이 바뀐 지금 세션을 본다.
  const checked = (async () => isRejectedAuthError((await getSupabase().auth.getUser(accessToken)).error))();
  try {
    return await Promise.race([checked, timedOut]);
  } catch (error) {
    // auth 오류로 돌려주지 않고 던진 예외(잠금 대기 초과 등)는 거절로 보지 않는다 — 세션을 남기고 호출자 흐름을 잇는다.
    console.error("[session]", error);
    return false;
  } finally {
    clearTimeout(timer);
  }
};

// 진행 중인 판정(토큰별) — 같은 토큰의 동시 401 은 하나를 나눠 쓰고, 로그인으로 바뀐 새 토큰은 옛 판정을 물려받지 않는다.
const inflight = new Map<string, Promise<boolean>>();

// 이 세션 토큰을 인증 서버(dev 는 가짜 GoTrue)에 물어 무효인지 — 셸 401 이 「토큰 무효」 인지 「멤버 없음」 인지 가른다.
export const sessionRejected = (accessToken: string): Promise<boolean> => {
  let check = inflight.get(accessToken);
  if (!check) {
    check = checkSession(accessToken).finally(() => {
      inflight.delete(accessToken);
    });
    inflight.set(accessToken, check);
  }
  return check;
};

// 서버 봉투의 세션 변경(D4) — set 은 받은 토큰으로 갈아 끼우고, clear 는 이 브라우저의 세션만 지운다.
export const applySessionChange = async (change: AuthSessionChange): Promise<void> => {
  const auth = getSupabase().auth;
  if (change.kind === "set") {
    const { error } = await auth.setSession({ access_token: change.session.access_token, refresh_token: change.session.refresh_token });
    if (error) throw error;
    return;
  }
  await auth.signOut({ scope: "local" });
};
